import type { HeldGift, SeatInfo } from "@app/protocol";
import { useStore } from "../store";
import { send } from "../ws/singleton";
import { CardSocial } from "./CardSocial";
import type { SeatSlot } from "./cardPlacement";
import { GiftBadge } from "./GiftBadge";
import { Ornament } from "./Ornament";
import { ReactionBubble } from "./ReactionBubble";
import { bubbleFor } from "./social";

/** What sits on a seated player's circle: the gift they hold and their latest reaction (review 0022). */
export function SeatOverlay({
  seat,
  held,
  sender,
  size,
  slot,
  gap,
}: {
  seat: SeatInfo;
  held: HeldGift | null;
  sender: SeatInfo | null;
  size: number;
  slot: SeatSlot;
  gap: number;
}) {
  const me = useStore((s) => s.member?.userId ?? null);
  const reactions = useStore((s) => s.reactions);
  const muted = useStore((s) => s.muted);
  const shown = bubbleFor(reactions, seat.userId, muted, Date.now());
  return (
    <Ornament>
      {held ? (
        <GiftBadge held={held} size={size} mine={seat.userId === me} sender={sender} />
      ) : null}
      {shown ? <ReactionBubble shown={shown} name={seat.name} slot={slot} gap={gap} /> : null}
    </Ornament>
  );
}

/** The card's table part for this seat, only for a viewer who sits at the table. */
export function SeatCardSocial({
  seat,
  seats,
  onSent,
}: {
  seat: SeatInfo;
  seats: readonly (SeatInfo | null)[];
  onSent: () => void;
}) {
  const me = useStore((s) => s.member?.userId ?? null);
  const muted = useStore((s) => s.muted);
  const set = useStore((s) => s.set);
  if (me === null || !seats.some((s) => s?.userId === me)) return null;
  const hidden = muted.includes(seat.userId);
  return (
    <CardSocial
      self={seat.userId === me}
      muted={hidden}
      onGift={(gift) => {
        send({ type: "gift", to: seat.userId, gift });
        onSent();
      }}
      onMute={() =>
        set({ muted: hidden ? muted.filter((m) => m !== seat.userId) : [...muted, seat.userId] })
      }
    />
  );
}
